import { useMemo, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import {
  createListInstance as createInstanceModel,
  resolveInstanceItems,
  createList as createCoreList,
  createListItem as createCoreListItem,
  mapItemDeep,
  removeItemDeep,
} from '@trackmind/core';
import type { TaskList, ListInstance, ListItem } from '@trackmind/core';

export type ListType = 'eternal' | 'template' | 'quick';

export interface CreateListInput {
  name: string;
  description?: string;
  list_type?: ListType;
  categories?: string[];
  linked_habit_ids?: string[];
  linked_project_ids?: string[];
  linked_goal_ids?: string[];
  is_pinned?: boolean;
  color?: string;
  icon?: string;
  default_sort?: 'order' | 'rating' | 'price' | 'priority' | 'due_date';
}

export interface UpdateListInput {
  name?: string;
  /** Whole-array replacement, used by drag-and-drop and the up/down arrows. */
  items?: ListItem[];
  description?: string;
  list_type?: ListType;
  categories?: string[];
  linked_habit_ids?: string[];
  linked_project_ids?: string[];
  linked_goal_ids?: string[];
  is_pinned?: boolean;
  color?: string;
  icon?: string;
  default_sort?: 'order' | 'rating' | 'price' | 'priority' | 'due_date';
  archived?: boolean;
  completed_at?: string | null;
}

export interface CreateListItemInput {
  text: string;
  quantity?: string;
  notes?: string;
  category?: string;
  url?: string;
  tags?: string[];
  children?: CreateListItemInput[];
  order?: number;
  parent_item_id?: string;
  price?: string;
  rating?: number;
  priority?: 'high' | 'medium' | 'low';
  location?: string;
  due_date?: string;
  source?: string;
  image_url?: string;
}

export interface UpdateListItemInput {
  text?: string;
  quantity?: string;
  notes?: string;
  category?: string;
  url?: string;
  tags?: string[];
  order?: number;
  completed_at?: string;
  price?: string;
  rating?: number;
  priority?: 'high' | 'medium' | 'low';
  location?: string;
  due_date?: string;
  source?: string;
  image_url?: string;
}

/** Core's item factory (shared with the assistant), placed at `order` unless one was given. */
function createListItem(data: CreateListItemInput, order: number): ListItem {
  return createCoreListItem({ ...data, order: data.order ?? order });
}

export function useLists(_includeArchived: boolean = false) {
  const { lists, saveList, deleteList: removeList } = useApp();

  const createList = useCallback(async (data: CreateListInput): Promise<TaskList> => {
    // Core's factory, shared with the assistant's "make a packing list".
    const newList = createCoreList(data);
    await saveList(newList);
    return newList;
  }, [saveList]);

  const updateList = useCallback(async (id: string, data: UpdateListInput): Promise<TaskList> => {
    const existing = lists.find(l => l.id === id);
    if (!existing) {
      throw new Error(`List ${id} not found`);
    }
    const updated: TaskList = {
      ...existing,
      ...data,
      updated_at: new Date().toISOString(),
    };
    await saveList(updated);
    return updated;
  }, [lists, saveList]);

  const archiveList = useCallback(async (id: string): Promise<TaskList> => {
    return updateList(id, { archived: true });
  }, [updateList]);

  const unarchiveList = useCallback(async (id: string): Promise<TaskList> => {
    return updateList(id, { archived: false });
  }, [updateList]);

  const completeList = useCallback(async (id: string): Promise<TaskList> => {
    return updateList(id, { completed_at: new Date().toISOString() });
  }, [updateList]);

  const uncompleteList = useCallback(async (id: string): Promise<TaskList> => {
    return updateList(id, { completed_at: null });
  }, [updateList]);

  const pinList = useCallback(async (id: string): Promise<TaskList> => {
    const list = lists.find(l => l.id === id);
    return updateList(id, { is_pinned: !list?.is_pinned });
  }, [lists, updateList]);

  const setListColor = useCallback(async (id: string, color: string | null): Promise<TaskList> => {
    return updateList(id, { color: color ?? undefined });
  }, [updateList]);

  const setListSort = useCallback(async (id: string, sortBy: 'order' | 'rating' | 'price' | 'priority' | 'due_date'): Promise<TaskList> => {
    return updateList(id, { default_sort: sortBy });
  }, [updateList]);

  // Item operations
  const addItem = useCallback(async (listId: string, data: CreateListItemInput): Promise<TaskList> => {
    const existing = lists.find(l => l.id === listId);
    if (!existing) {
      throw new Error(`List ${listId} not found`);
    }

    // If parent_item_id is specified, add as a child item
    if (data.parent_item_id) {
      const addChildRecursive = (items: ListItem[]): ListItem[] => {
        return items.map(item => {
          if (item.id === data.parent_item_id) {
            const newChild = createListItem(data, item.children?.length ?? 0);
            return {
              ...item,
              children: [...(item.children ?? []), newChild],
            };
          }
          if (item.children && item.children.length > 0) {
            return { ...item, children: addChildRecursive(item.children) };
          }
          return item;
        });
      };

      const updated: TaskList = {
        ...existing,
        items: addChildRecursive(existing.items),
        updated_at: new Date().toISOString(),
      };
      await saveList(updated);
      return updated;
    }

    // Otherwise add as a root item
    const newItem = createListItem(data, existing.items.length);
    const updated: TaskList = {
      ...existing,
      items: [...existing.items, newItem],
      updated_at: new Date().toISOString(),
    };
    await saveList(updated);
    return updated;
  }, [lists, saveList]);

  const updateItem = useCallback(async (listId: string, itemId: string, data: UpdateListItemInput): Promise<TaskList> => {
    const existing = lists.find(l => l.id === listId);
    if (!existing) {
      throw new Error(`List ${listId} not found`);
    }

    const updateItemRecursive = (items: ListItem[]): ListItem[] => {
      return items.map(item => {
        if (item.id === itemId) {
          return { ...item, ...data };
        }
        if (item.children && item.children.length > 0) {
          return { ...item, children: updateItemRecursive(item.children) };
        }
        return item;
      });
    };

    const updated: TaskList = {
      ...existing,
      items: updateItemRecursive(existing.items),
      updated_at: new Date().toISOString(),
    };
    await saveList(updated);
    return updated;
  }, [lists, saveList]);

  const toggleItem = useCallback(async (listId: string, itemId: string): Promise<TaskList> => {
    const existing = lists.find(l => l.id === listId);
    if (!existing) {
      throw new Error(`List ${listId} not found`);
    }

    const toggleItemRecursive = (items: ListItem[]): ListItem[] => {
      return items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            completed_at: item.completed_at ? null : new Date().toISOString(),
          };
        }
        if (item.children && item.children.length > 0) {
          return { ...item, children: toggleItemRecursive(item.children) };
        }
        return item;
      });
    };

    const updated: TaskList = {
      ...existing,
      items: toggleItemRecursive(existing.items),
      updated_at: new Date().toISOString(),
    };
    await saveList(updated);
    return updated;
  }, [lists, saveList]);

  const deleteItem = useCallback(async (listId: string, itemId: string): Promise<TaskList> => {
    const existing = lists.find(l => l.id === listId);
    if (!existing) {
      throw new Error(`List ${listId} not found`);
    }

    const deleteItemRecursive = (items: ListItem[]): ListItem[] => {
      return items
        .filter(item => item.id !== itemId)
        .map(item => ({
          ...item,
          children: item.children ? deleteItemRecursive(item.children) : [],
        }));
    };

    const updated: TaskList = {
      ...existing,
      items: deleteItemRecursive(existing.items),
      updated_at: new Date().toISOString(),
    };
    await saveList(updated);
    return updated;
  }, [lists, saveList]);

  // Sort function - pinned first, then by updated_at
  const sortLists = useCallback((listArr: TaskList[]) =>
    [...listArr].sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    }), []);

  // Filter helpers - now sorted with pinned first
  const eternalLists = useMemo(() => sortLists(lists.filter(l => l.list_type === 'eternal' && !l.archived)), [lists, sortLists]);
  const templateLists = useMemo(() => sortLists(lists.filter(l => l.list_type === 'template' && !l.archived)), [lists, sortLists]);
  const quickLists = useMemo(() => sortLists(lists.filter(l => l.list_type === 'quick' && !l.archived)), [lists, sortLists]);
  const archivedLists = useMemo(() => lists.filter(l => l.archived), [lists]);
  const pinnedLists = useMemo(() => lists.filter(l => l.is_pinned && !l.archived), [lists]);
  const completedLists = useMemo(() => sortLists(lists.filter(l => l.completed_at && !l.archived)), [lists, sortLists]);

  return {
    lists: sortLists(lists.filter(l => !l.archived)),
    eternalLists,
    templateLists,
    quickLists,
    archivedLists,
    pinnedLists,
    completedLists,
    loading: false,
    error: null,
    refetch: () => Promise.resolve(),
    createList,
    updateList,
    deleteList: removeList,
    archiveList,
    unarchiveList,
    completeList,
    uncompleteList,
    pinList,
    setListColor,
    setListSort,
    addItem,
    updateItem,
    toggleItem,
    deleteItem,
  };
}

export function useListInstances(listId: string, _includeArchived: boolean = false) {
  const { listInstances, saveListInstance, deleteListInstance: removeInstance, lists } = useApp();

  // Filter instances for this list
  const instances = useMemo(() =>
    listInstances.filter(i => i.list_id === listId),
    [listInstances, listId]);

  /**
   * The effective items of a started checklist.
   *
   * A run owns its items: starting a checklist snapshots the template, and
   * from then on the instance is a full list of its own — reorder, edit,
   * sub-items and delete all apply to the run without touching the template.
   * Runs created before that existed stored only a checked-state map against
   * the live template; those are resolved here on read, and the first write
   * persists the resolved snapshot so they migrate in place.
   */
  const itemsFor = useCallback((instance: ListInstance): ListItem[] =>
    resolveInstanceItems(instance, lists.find(l => l.id === instance.list_id) ?? null),
    [lists]);

  /** Every item mutation funnels through here, so legacy runs migrate once. */
  const writeItems = useCallback(async (
    instanceId: string,
    mutate: (items: ListItem[]) => ListItem[],
  ): Promise<ListInstance> => {
    const existing = listInstances.find(i => i.id === instanceId);
    if (!existing) {
      throw new Error(`Instance ${instanceId} not found`);
    }
    const updated: ListInstance = { ...existing, items: mutate(itemsFor(existing)) };
    await saveListInstance(updated);
    return updated;
  }, [listInstances, itemsFor, saveListInstance]);

  const createInstance = useCallback(async (name?: string): Promise<ListInstance> => {
    const list = lists.find(l => l.id === listId);
    const newInstance = createInstanceModel(
      { list_id: listId, name: name ?? null },
      list?.name,
      list?.items ?? [],
    );
    await saveListInstance(newInstance);
    return newInstance;
  }, [listId, lists, saveListInstance]);

  const toggleItem = useCallback((instanceId: string, itemId: string): Promise<ListInstance> =>
    writeItems(instanceId, items => mapItemDeep(items, itemId, item => ({
      ...item,
      completed_at: item.completed_at ? null : new Date().toISOString(),
    }))), [writeItems]);

  /** Add a root item, or a sub-item when `parent_item_id` is given. */
  const addItem = useCallback((instanceId: string, data: CreateListItemInput): Promise<ListInstance> =>
    writeItems(instanceId, items => {
      if (data.parent_item_id) {
        return mapItemDeep(items, data.parent_item_id, parent => ({
          ...parent,
          children: [
            ...(parent.children ?? []),
            createListItem(data, parent.children?.length ?? 0),
          ],
        }));
      }
      return [...items, createListItem(data, items.length)];
    }), [writeItems]);

  const updateItem = useCallback((
    instanceId: string,
    itemId: string,
    data: UpdateListItemInput,
  ): Promise<ListInstance> =>
    writeItems(instanceId, items => mapItemDeep(items, itemId, item => ({ ...item, ...data }))),
    [writeItems]);

  const deleteItem = useCallback((instanceId: string, itemId: string): Promise<ListInstance> =>
    writeItems(instanceId, items => removeItemDeep(items, itemId)), [writeItems]);

  /** Persist a reordered item array (up/down arrows and drag both use this). */
  const reorderItems = useCallback((
    instanceId: string,
    orderedItems: ListItem[],
  ): Promise<ListInstance> =>
    writeItems(instanceId, () => orderedItems.map((item, idx) => ({ ...item, order: idx }))),
    [writeItems]);

  const completeInstance = useCallback(async (instanceId: string): Promise<ListInstance> => {
    const existing = listInstances.find(i => i.id === instanceId);
    if (!existing) {
      throw new Error(`Instance ${instanceId} not found`);
    }
    const updated: ListInstance = {
      ...existing,
      completed_at: new Date().toISOString(),
    };
    await saveListInstance(updated);
    return updated;
  }, [listInstances, saveListInstance]);

  const archiveInstance = useCallback(async (instanceId: string): Promise<ListInstance> => {
    const existing = listInstances.find(i => i.id === instanceId);
    if (!existing) {
      throw new Error(`Instance ${instanceId} not found`);
    }
    const updated: ListInstance = {
      ...existing,
      archived_at: new Date().toISOString(),
    };
    await saveListInstance(updated);
    return updated;
  }, [listInstances, saveListInstance]);

  // Filter helpers
  const activeInstances = useMemo(() => instances.filter(i => !i.archived_at), [instances]);
  const completedInstances = useMemo(() => instances.filter(i => i.completed_at && !i.archived_at), [instances]);
  const archivedInstances = useMemo(() => instances.filter(i => i.archived_at), [instances]);

  return {
    instances,
    activeInstances,
    completedInstances,
    archivedInstances,
    loading: false,
    error: null,
    refetch: () => Promise.resolve(),
    itemsFor,
    createInstance,
    toggleItem,
    addItem,
    updateItem,
    deleteItem,
    reorderItems,
    completeInstance,
    archiveInstance,
    deleteInstance: removeInstance,
  };
}
